import React, { useState, useEffect } from "react";
import { fetchAllEnquiries, updateInquiryStatus, deleteInquiry } from "../../functions/Enquiry";
import { db } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";

const Inquiries = ({ limit, hideHeader, hideFilter }) => {
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(null); 
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Helper to format date as DD/MM/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d)) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Fetch inquiries when component mounts
  useEffect(() => {
    const getInquiries = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch travel plan enquiries from "enquiries" collection (lowercase)
        const travelEnquiriesResponse = await fetchAllEnquiries();
        let allEnquiries = [];
        
        if (travelEnquiriesResponse.success) {
          allEnquiries = [...travelEnquiriesResponse.data];
        } else {
          // Error handling happens in the catch block
        }
        
        // Fetch booking enquiries from "Enquiries" collection (uppercase)
        try {
          const bookingEnquiriesCollectionRef = collection(db, 'Enquiries');
          const bookingQuerySnapshot = await getDocs(bookingEnquiriesCollectionRef);
          
          // Process each document in the collection
          bookingQuerySnapshot.forEach((doc) => {
            const data = doc.data();
            
            // Process fields that begin with "entries."
            Object.keys(data).forEach(key => {
              if (key.startsWith('entries.')) {
                const entryId = key.split('.')[1]; // Get the ID part
                const entryData = data[key];
                
                // Format booking enquiry data to match the expected structure
                allEnquiries.push({
                  id: entryId,
                  date: entryData.timestamp ? new Date(entryData.timestamp.toDate()).toLocaleDateString() : 'N/A',
                  name: entryData.contactInfo?.name || 'Unknown',
                  email: entryData.contactInfo?.email || 'Unknown',
                  subject: entryData.tripTitle || 'Tour Booking',
                  status: entryData.status || 'New',
                  contactInfo: entryData.contactInfo,
                  details: {
                    tripTitle: entryData.tripTitle,
                    tripDuration: entryData.tripDuration,
                    pricingType: entryData.pricingType,
                    price: entryData.price,
                    batchDate: entryData.batchDate
                  },
                  rawData: entryData,
                  type: 'booking' // Add a type flag to differentiate booking enquiries
                });
              }
            });
          });
          
        } catch (bookingError) {
          // Error is caught and handled in the outer catch block
        }
        
        // Sort all enquiries by date (newest first)
        allEnquiries.sort((a, b) => {
          if (a.date === 'N/A' || b.date === 'N/A') return 0;
          return new Date(b.date) - new Date(a.date);
        });
        
        setInquiries(allEnquiries);
        setFilteredInquiries(allEnquiries);
        
      } catch (err) {
        setError("Failed to fetch inquiries. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    getInquiries();
  }, []);

  // Handle status change
  const handleStatusChange = async (inquiry, newStatus) => {
    try {
      // Show loading indicator for this specific button
      setLoadingStatus(newStatus);
      
      // Call the API function to update status in Firestore
      const result = await updateInquiryStatus(inquiry.id, newStatus, inquiry.type || 'inquiry');
      
      if (result.success) {
        // Update local state only after successful database update
        const updatedInquiries = inquiries.map(item =>
          item.id === inquiry.id ? { ...item, status: newStatus } : item
        );

        setInquiries(updatedInquiries);
        setFilteredInquiries(
          statusFilter === "All Statuses"
            ? updatedInquiries
            : updatedInquiries.filter(item => item.status === statusFilter)
        );

        // If modal is open, update the selected inquiry
        if (selectedInquiry && selectedInquiry.id === inquiry.id) {
          setSelectedInquiry({...selectedInquiry, status: newStatus});
        }
      } else {
        // Show error notification
        setError(`Failed to update status: ${result.message}`);
        setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
      }
    } catch (err) {
      setError("An error occurred while updating the status.");
      setTimeout(() => setError(null), 3000);
    } finally {
      // Clear the loading status
      setLoadingStatus(null);
      
      if (showModal) {
        setShowModal(false);
        setSelectedInquiry(null);
      }
    }
  };

  // Handle delete inquiry
  const handleDeleteInquiry = async () => {
    try {
      setDeleteLoading(true);
      
      const result = await deleteInquiry(selectedInquiry.id, selectedInquiry.type || 'inquiry');
      
      if (result.success) {
        // Remove the deleted inquiry from local state
        const updatedInquiries = inquiries.filter(item => item.id !== selectedInquiry.id);
        setInquiries(updatedInquiries);
        setFilteredInquiries(
          statusFilter === "All Statuses"
            ? updatedInquiries
            : updatedInquiries.filter(item => item.status === statusFilter)
        );
        
        // Close the modal and confirmation
        setShowDeleteConfirm(false);
        setShowModal(false);
        setSelectedInquiry(null);
      } else {
        // Show error notification
        setError(`Failed to delete inquiry: ${result.message}`);
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      setError("An error occurred while deleting the inquiry.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Filter inquiries based on status
  const handleStatusFilter = (e) => {
    const status = e.target.value;
    setStatusFilter(status);

    if (status === "All Statuses") {
      setFilteredInquiries(inquiries);
    } else {
      setFilteredInquiries(
        inquiries.filter((inquiry) => inquiry.status === status)
      );
    }
  };

  // Apply filter when clicked
  const applyFilter = () => {
    if (statusFilter === "All Statuses") {
      setFilteredInquiries(inquiries);
    } else {
      setFilteredInquiries(
        inquiries.filter((inquiry) => inquiry.status === statusFilter)
      );
    }
  };

  // Handle view inquiry
  const handleViewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedInquiry(null);
  };

  return (
    <div className="inquiries-section fadeIn">
      <div className="admin-card">
        {!hideHeader && (
          <div className="card-header">
            <h3>
              Customer Inquiries 
              <span className="badge bg-secondary ms-2">{filteredInquiries.length}</span>
            </h3>
            {!hideFilter && (
              <div className="d-flex">
                <select
                  className="form-select form-select-sm me-2"
                  value={statusFilter}
                  onChange={handleStatusFilter}
                >
                  <option>All Statuses</option>
                  <option>New</option>
                  <option>Pending</option>
                  <option>Resolved</option>
                </select>
                <button className="btn btn-sm btn-primary" onClick={applyFilter}>
                  <i className="fas fa-filter me-1"></i> Filter
                </button>
              </div>
            )}
          </div>
        )}
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading inquiries...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : (limit ? filteredInquiries.slice(0, limit) : filteredInquiries).length === 0 ? (
            <div className="alert alert-info" role="alert">
              No inquiries found.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm inquiries-table-small">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(limit ? filteredInquiries.slice(0, limit) : filteredInquiries).map((inquiry, index) => (
                    <tr key={inquiry.id}>
                      <td>#{inquiry.id}</td>
                      <td>{inquiry.name}</td>
                      <td>{inquiry.email}</td>
                      <td>{inquiry.subject}</td>
                      <td>{formatDate(inquiry.date)}</td>
                      <td>
                        <span className={`badge bg-${
                          inquiry.status === "New" ? "danger" :
                          inquiry.status === "Pending" ? "warning" : "success"
                        }`}>
                          {inquiry.status}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleViewInquiry(inquiry)}
                        >View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Inquiry Detail Modal */}
      {showModal && selectedInquiry && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content inquiries-modal-small">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedInquiry.type === 'booking' ? 'Booking' : 'Inquiry'} #{selectedInquiry.id}
                  <span className={`badge bg-${
                    selectedInquiry.status === "New" ? "danger" :
                    selectedInquiry.status === "Pending" ? "warning" : "success"
                  } ms-2`}>
                    {selectedInquiry.status}
                  </span>
                </h5>
                <button type="button" className="btn-close" onClick={closeModal}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="mb-0">Contact Information</h6>
                      </div>
                      <div className="card-body text-start">
                        <p><strong>Name:</strong> {selectedInquiry.contactInfo?.name}</p>
                        <p><strong>Email:</strong> {selectedInquiry.contactInfo?.email}</p>
                        <p><strong>Phone:</strong> {selectedInquiry.contactInfo?.phone}</p>
                        {/* Show any extra contact fields */}
                        {Object.entries(selectedInquiry.contactInfo || {})
                          .filter(([key]) => !['name', 'email', 'phone'].includes(key))
                          .map(([key, value]) => (
                            <p key={key}><strong>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:</strong> {value}</p>
                        ))}
                        <p><strong>Date Submitted:</strong> {formatDate(selectedInquiry.date)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <div className="card h-100">
                      <div className="card-header">
                        <h6 className="mb-0">
                          {selectedInquiry.type === 'booking' ? 'Booking Details' : 'Trip Details'}
                        </h6>
                      </div>
                      <div className="card-body text-start">
                        {selectedInquiry.type === 'booking' ? (
                          // Booking-specific details
                          <>
                            <p><strong>Tour:</strong> {selectedInquiry.details.tripTitle}</p>
                            <p><strong>Duration:</strong> {selectedInquiry.details.tripDuration}</p>
                            <p><strong>Accommodation:</strong> {selectedInquiry.details.pricingType}</p>
                            <p><strong>Price:</strong> ₹{selectedInquiry.details.price}</p>
                            <p><strong>Batch Date:</strong> {selectedInquiry.details.batchDate}</p>
                            <p><strong>Number of Persons:</strong> {selectedInquiry.contactInfo?.personCount || '1'}</p>
                          </>
                        ) : (
                          // General inquiry details (show all captured fields)
                          <>
                            {/* Map trip type to user-friendly name */}
                            {selectedInquiry.details?.tripType && (
                              <p><strong>Trip Type:</strong> {
                                {
                                  'backpacking': 'Backpacking Trips',
                                  'biking': 'Biking Trips',
                                  'treks': 'Treks',
                                  'corporate': 'Corporate Tour',
                                  'weekend': 'Weekend Getaways',
                                  'honeymoon': 'Honeymoon Tour',
                                  'domestic': 'Domestic Packages',
                                  'international': 'International Tour'
                                }[selectedInquiry.details.tripType] || selectedInquiry.details.tripType
                              }</p>
                            )}
                            
                            {/* Format travel date */}
                            {selectedInquiry.details?.travelDate && (
                              <p><strong>Travel Date:</strong> {formatDate(selectedInquiry.details.travelDate)}</p>
                            )}
                            
                            {/* Display traveller count */}
                            {selectedInquiry.details?.travellerCount && (
                              <p><strong>Number of Travellers:</strong> {String(selectedInquiry.details.travellerCount)}</p>
                            )}
                            
                            {/* Only show message if it's not empty */}
                            {selectedInquiry.details?.message && selectedInquiry.details.message.trim() !== '' && (
                              <p><strong>Message:</strong> {String(selectedInquiry.details.message)}</p>
                            )}
                            
                            {/* Show any other fields from details */}
                            {Object.entries(selectedInquiry.details || {})
                              .filter(([key]) => !['tripType', 'travelDate', 'travellerCount', 'message'].includes(key))
                              .map(([key, value]) => {
                                // Skip rendering if value is empty or null
                                if (value === null || value === undefined || value === '') {
                                  return null;
                                }
                                
                                // Safely convert value to display format
                                let displayValue;
                                try {
                                  if (Array.isArray(value)) {
                                    displayValue = value.join(', ');
                                  } else if (typeof value === 'object' && value !== null) {
                                    displayValue = JSON.stringify(value);
                                  } else if (typeof value === 'boolean') {
                                    displayValue = value ? 'Yes' : 'No';
                                  } else {
                                    displayValue = String(value);
                                  }
                                } catch (error) {
                                  displayValue = '[Unable to display]';
                                }
                                
                                return (
                                  <p key={key}>
                                    <strong>
                                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                                    </strong>{" "}
                                    {displayValue}
                                  </p>
                                );
                              })}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="status-change-container me-auto">
                  <span className="me-2">Change status:</span>
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleStatusChange(selectedInquiry, "New")}
                      disabled={selectedInquiry.status === "New" || loadingStatus !== null}
                    >
                      {loadingStatus === "New" ? (
                        <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Processing...</>
                      ) : (
                        "New"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-warning"
                      onClick={() => handleStatusChange(selectedInquiry, "Pending")}
                      disabled={selectedInquiry.status === "Pending" || loadingStatus !== null}
                    >
                      {loadingStatus === "Pending" ? (
                        <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Processing...</>
                      ) : (
                        "Pending"
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-success"
                      onClick={() => handleStatusChange(selectedInquiry, "Resolved")}
                      disabled={selectedInquiry.status === "Resolved" || loadingStatus !== null}
                    >
                      {loadingStatus === "Resolved" ? (
                        <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Processing...</>
                      ) : (
                        "Resolved"
                      )}
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-danger me-2"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={loadingStatus !== null || deleteLoading}
                >
                  <i className="fas fa-trash-alt me-1"></i> Delete
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeModal}
                  disabled={loadingStatus !== null || deleteLoading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedInquiry && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1060 }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Deletion</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteConfirm(false)} disabled={deleteLoading}></button>
              </div>
              <div className="modal-body text-start">
                <p>Are you sure you want to delete this inquiry? This action cannot be undone.</p>
                <div className="alert alert-danger" style={{ backgroundColor: 'rgba(255, 82, 82, 0.15)', color: 'var(--dark-text)', borderColor: 'rgba(255, 82, 82, 0.3)' }}>
                  <strong>Warning:</strong> This will permanently remove the inquiry from the database.
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleDeleteInquiry}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Deleting...</>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inquiries;
